import React, { useEffect, useState } from 'react'
import ListItemStyled from './styled'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Lock, PencilLine, Trash2 } from 'lucide-react'
import { CustomAlert, ActionsCell, ActionsGroup } from '../../atoms'
import { tokens } from '../../../assets/tokens'
import { CircularProgress } from '@material-ui/core'
import ModalEditPhase from '../modal-edit-phase'
import ModalEditState from '../modal-edit-state'
import ModalDeleteState from '../modal-delete-state'
import ModalDeletePhase from '../modal-delete-phase'

const ListItem = ({ i18n, item, onSubmitEditPhase, elementType, onSubmitEditState, phaseId, onSubmitDelete, phasesData, module }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [showAlert, setShowAlert] = useState(false)

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id })

  useEffect(() => {
    if (showAlert) {
      setTimeout(() => {
        setShowAlert(false)
      }, 8000)
    }
  }, [showAlert])

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  const handleEditClick = () => {
    setIsModalOpen(true)
  }

  const handleDeletePhaseClick = async item => {
    const phase = phasesData.data.find(phaseData => phaseData.id === item.id)
    if (phase) {
      const hasStates = phase.status_candidates.length > 0

      if (hasStates) {
        setShowAlert(true)
      } else {
        setItemToDelete(item)
        setIsDeleteModalOpen(true)
      }
    }
  }

  const handleDeleteStatusClick = async item => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  }

  const confirmDelete = async itemToDelete => {
    setIsLoading(true);
    // Sin catch: si falla, el error llega al modal y este sigue abierto
    try {
      if (elementType === 'phase') {
        return await onSubmitDelete(itemToDelete);
      } else {
        const itemWithPhaseId = {
          ...itemToDelete,
          phaseId
        };
        return await onSubmitDelete(itemWithPhaseId);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const isCustomPhase = item.type === 'CUSTOM'

  // Devuelven la promesa para que el modal espere a la respuesta antes de cerrarse
  const handleSubmitEditState = updatedState => onSubmitEditState(updatedState)

  const handleSubmitEditPhase = updatedPhase => onSubmitEditPhase(updatedPhase)

  return (
    <ListItemStyled>
      <div style={style} ref={setNodeRef} className="dragHandle">
        <div className="listItem">
          {/* Solo las fases y estados personalizados se arrastran; los predefinidos llevan candado */}
          {isCustomPhase ? (
            <span className="dragIcon" {...attributes} {...listeners}>
              <GripVertical size={18} color={tokens.color.text.secondary} />
            </span>
          ) : (
            <span className="lockIcon">
              <Lock size={18} color={tokens.color.text.secondary} />
            </span>
          )}
          {elementType === 'state' && item.color && <span className="colorDot" style={{ backgroundColor: item.color }} />}
          <span className="content">{item.name}</span>
        </div>
        <ActionsGroup>
          <ActionsCell icon={<PencilLine size={18} color={tokens.color.text.secondary} />} tooltip={i18n.boxEditableInfo.edit} onClick={handleEditClick} />
          {isCustomPhase &&
            (isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <ActionsCell
                icon={<Trash2 size={18} color={tokens.color.error.main} />}
                tooltip={i18n.phases_states.buttonDelete}
                onClick={() => (elementType === 'phase' ? handleDeletePhaseClick(item) : handleDeleteStatusClick(item))}
              />
            ))}
        </ActionsGroup>
        {elementType === 'phase' ? (
          <>
            <ModalEditPhase
              open={isModalOpen}
              handleClose={() => setIsModalOpen(false)}
              initialValue={item.name}
              onSubmitEditPhase={handleSubmitEditPhase}
              itemId={item.id}
              allPhasesData={phasesData.data}
              color={item.color}
            />

            <ModalDeletePhase
              open={isDeleteModalOpen}
              handleClose={() => setIsDeleteModalOpen(false)}
              initialValue={item.name}
              onSubmitDelete={() => confirmDelete(itemToDelete)}
              itemId={item.id}
              allPhasesData={phasesData.data}
              color={item.color}
            />
          </>
        ) : (
          <>
            <ModalEditState
              open={isModalOpen}
              handleClose={() => setIsModalOpen(false)}
              phaseId={phaseId}
              onSubmitEditState={handleSubmitEditState}
              statusItem={item}
              phasesData={phasesData}
              color={item.color}
              description={item.description}
            />
            <ModalDeleteState
              open={isDeleteModalOpen}
              handleClose={() => setIsDeleteModalOpen(false)}
              onSubmitDelete={() => confirmDelete(itemToDelete)}
              item={item}
              module={module}
            />
          </>
        )}
      </div>
      {showAlert && (
        <CustomAlert
          severity="error"
          alertTitle={i18n.phases_states.alertTitleCannotDeletePhase}
          message={i18n.phases_states.messageAlertCannotDeletePhase}
        />
      )}
    </ListItemStyled>
  )
}

export default ListItem
